import RadioList from 'design-v2/common/RadioList';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ActionButton from 'design-v2/common/ActionButton';
import AdminService, { NumberOfAccounts } from 'external-services/diary-admin-service';
import style from './style.module.css';
import { appValues } from 'appRoot/app.constants';
import classNames from 'classnames';
import { IAccount } from 'shared-types/DiaryTypes';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import {
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import ReactUtilsService, { useThrottle } from 'external-services/react-utils-service';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';
import { IVenueSelectorProps } from './types';
import { orderBy } from 'lodash';

const NS = 'VenueSelector';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxHeight: 'calc(100vh - 600px)',
    minHeight: 350,
    overflowY: 'auto',
  },
  loadingWrap: {
    width: '100%',
    height: 200,
    display: 'flex',
    justifyContent: 'center',
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  loading: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
}));

function Loading() {
  const classes = useStyles();
  return (
    <div className={classes.loadingWrap}>
      <CircularProgress />
    </div>
  );
}

export default function VenueSelector(props: IVenueSelectorProps) {
  const {
    account,
    isSwitchVenue,
    allAccountsForUser,
    isAdminUser,
    quantity,
    searchString,
    venueHandler,
    logoutVenue,
    handleSearchAccounts,
    handleLoadMore,
    cancelHandler,
  } = props;
  const classes = useStyles();
  const [loaded, setLoaded] = useState(false);
  const searchInputRef = useRef(null);

  const [accountCounter, setAccountCounter] = React.useState(NumberOfAccounts);
  const [isLoadingAccounts, setIsLoadingAccounts] = React.useState(false);
  const [expand, setExpanded] = React.useState('');

  const allActiveAccounts: IAccount[] = useMemo(() => {
    return allAccountsForUser?.filter((account) => account.active);
  }, [allAccountsForUser]);

  const searchAccounts: string = useMemo(() => {
    return searchString;
  }, [searchString]);

  const _venueHandler = async (venueID: number, isPosActive: boolean, accountId?: string) => {
    setIsLoadingAccounts(true);//prevent double click, consider this is end of the login process, so didn't set this to false again
    AdminService.setIPosStatusForVenue(isPosActive); // Setting the IPos status value in the local storage - to be used again in the container to load Ipos or not
    await venueHandler(venueID, accountId);
  };

  const _handleSearchAccounts = async (searchValue: string) => {
    setIsLoadingAccounts(true);
    await handleSearchAccounts(searchValue);
    setAccountCounter(NumberOfAccounts);
    setIsLoadingAccounts(false);
    searchInputRef?.current.focus();
  };

  const _handleLoadMore = async () => {
    setIsLoadingAccounts(true);
    setAccountCounter(accountCounter + NumberOfAccounts);
    const loadMoreAccountsInfo = await handleLoadMore(accountCounter, searchAccounts);
    setIsLoadingAccounts(false);
    scrollIntoView(loadMoreAccountsInfo.data[0].id);
  };

  const scrollIntoView = (counter: string) => {
    const scrollViewRefCounter = `panel1bh-header_${counter}`;
    const scrollRef = document.querySelectorAll(`div[id="${scrollViewRefCounter}"]`);
    ReactUtilsService.scrollGridItemIntoView(scrollRef[0] as HTMLDivElement);
  };
  const delayedSetLoaded = () => {
    setTimeout(() => {
      setLoaded(true);
    }, 300);
  };

  useEffect(() => {
    // If Venue exists in local storage we show the venue and NOT the venue selector screen
    if (!isSwitchVenue && AdminService.getVenue()) {
      const localStorageVenue = AdminService.getVenue();
      const localIPosValue = AdminService.getIPosStatusForVenue();
      _venueHandler(localStorageVenue.id, localIPosValue);
    }
    if (
      isAdminUser &&
      allAccountsForUser?.length === 1 &&
      allAccountsForUser[0]?.ownedVenues.length === 1
    ) {
      _venueHandler(allAccountsForUser[0]?.ownedVenues[0].id, allAccountsForUser[0]?.ownedVenues[0].isPosActive, allAccountsForUser[0]?.id);
    }
    if (!isAdminUser && account?.venues?.length === 1) {
      _venueHandler(account.venues[0]?.id, account.venues[0]?.isPosActive);
    } else {
      delayedSetLoaded();
    }
  }, []);

  const throttledLoadMore = useThrottle(_handleLoadMore, 500);
  const throttledSearchAccounts = useThrottle(_handleSearchAccounts, 1000);

  return (
    <div
      className={classNames({
        [style.rootBox]: true,
        [style.flexCenterVertical]: true,
        [style.fadeIn]: true,
        [style.loaded]: loaded,
        [style.selected]: loaded === false,
      })}
    >
      <div
        className={classNames({
          [style.contentBox]: true,
          [style.slideIn]: true,
          [style.loaded]: loaded,
          [style.selected]: loaded === false,
        })}
      >
        <div
          className={classNames({
            [style.marginMedium]: true,
            [style.flexCenterVertical]: true,
          })}
        >
          <img src="images/new-logo-stacked.svg" alt="logo" className={style.logoContainer} />
        </div>
        {isLoadingAccounts && <Loading />}
        {!isLoadingAccounts && account && (
          <>
            {/*TODO: need to get logo of each venue */}
            {!isAdminUser && account.venues && (
              <RadioList
                items={account.venues.map((venue) => {
                  // return {title: venue.name, children: <img src={venue.logoUrl} alt={''}/>}
                  return { title: venue.name };
                })}
                handleClick={(e) =>
                  _venueHandler(account.venues[e]?.id, account.venues[e]?.isPosActive)
                }
              />
            )}

            {isAdminUser && (
              <>
                <div>
                  <TextField
                    fullWidth
                    inputRef={searchInputRef}
                    id="input-with-icon-textfield"
                    placeholder="Search Accounts"
                    defaultValue={searchAccounts}
                    className={style.searchInput}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => throttledSearchAccounts('')}>
                          <ClearIcon />
                        </IconButton>
                      ),
                    }}
                    onChange={(event) => {
                      throttledSearchAccounts(event.target.value);
                    }}
                  />
                </div>
                {allActiveAccounts?.length > 0 && (
                  <div className={classes.root}>
                    {allActiveAccounts.map((account: IAccount, index) => {
                      const ownedVenues = orderBy(account.ownedVenues, ['name'], ['asc']);
                      return (
                        <ExpansionPanel
                          key={index}
                          expanded={expand === account.id}
                          onChange={() => {
                            if (expand === account.id) {
                              // collapse
                              setExpanded('');
                            } else {
                              setExpanded(account.id);
                              scrollIntoView(account.id);
                            }
                          }}
                        >
                          <ExpansionPanelSummary
                            expandIcon={<ExpandMore />}
                            aria-controls="panel1bh-content"
                            id={'panel1bh-header_' + account.id}
                          >
                            <Typography className={classes.heading}>
                              {account.businessName}
                            </Typography>
                          </ExpansionPanelSummary>
                          <ExpansionPanelDetails>
                            <div style={{ width: '100%' }}>
                              <RadioList
                                items={
                                  ownedVenues
                                    .map((venue) => {
                                      return { title: venue.name };
                                    }
                                )}
                                handleClick={(e) => _venueHandler(ownedVenues[e]?.id, ownedVenues[e]?.isPosActive, account.id)}
                              />
                            </div>
                          </ExpansionPanelDetails>
                        </ExpansionPanel>
                      );
                    })}
                  </div>
                )}

                <div
                  className={classNames({
                    [style.marginMedium]: true,
                    [style.flexCenterVertical]: true,
                  })}
                >
                  {accountCounter <= quantity && (
                    <div className={style.btnWrap}>
                      <ActionButton
                        variant={'primary'}
                        size={'small'}
                        text={'Load More'}
                        handleClick={throttledLoadMore}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
            <div
              className={classNames({
                [style.marginMedium]: true,
                [style.flexCenterVertical]: true,
              })}
            >
              <div className={style.btnWrap}>
                {!!AdminService.getVenue()?.id && (
                  <ActionButton
                    variant={'primary'}
                    size={'regular'}
                    text={'Cancel'}
                    handleClick={cancelHandler}
                  />
                )}
                {!props.hideLogout && (
                  <ActionButton
                    variant={'secondary'}
                    size={'regular'}
                    text={'Log out'}
                    handleClick={logoutVenue}
                  />
                )}
              </div>
              <div
                className={classNames({
                  [style.marginMedium]: true,
                  [style.versionStyle]: true,
                })}
              >
                {`Version ${appValues.VERSION_SHORT}`}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
